import * as am5 from '@amcharts/amcharts5'
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated'
import * as am5xy from '@amcharts/amcharts5/xy'
import { useEffect, useRef } from 'react'

import { AdminFilters } from '#components/adminFilters/AdminFiltersPanel.js'
import { ReportDataWrapper } from '#components/ReportDataWrapper'
import { ReportFilterControls } from '#components/ReportFilterControls'
import { useFetch } from '#hooks/useAdminResource'
import { useCarrierRequestFilters } from '#hooks/useCarrierRequestFilters'

import { CarrierRequestServiceMultiSelect } from './CarrierRequestServiceMultiSelect'

interface CarrierActivityStats {
  stepMinutes: number
  dailyByCarrier: {
    date: string
    count: number
    byCarrier: {
      carrierCode: string
      count: number
    }[]
  }[]
}

const GOLDEN_RATIO = 0.618033988749895

// Generates visually distinct colors by distributing hues evenly via the
// golden ratio, then alternating saturation/lightness to avoid adjacent
// series looking too similar.
const getSeriesColor = (index: number, total: number): am5.Color => {
  const hue = total <= 1 ? 0.39 : (index * GOLDEN_RATIO) % 1
  const saturation = index % 2 === 0 ? 0.75 : 0.55
  const lightness = index % 3 === 0 ? 0.45 : index % 3 === 1 ? 0.6 : 0.35
  return am5.Color.fromHSL(hue, saturation, lightness)
}

const CarrierRequestsReport = () => {
  const { localFilters, setLocalFilters, apiFilters } =
    useCarrierRequestFilters({
      defaultService: ['tracking'],
      defaultRange: '7d',
    })

  const {
    data: stats,
    isLoading,
    error,
    refetch: refresh,
  } = useFetch<CarrierActivityStats>(
    'reports/carrier-requests/daily',
    apiFilters,
  )

  const chartContainerRef = useRef<HTMLDivElement | null>(null)
  const chartRef = useRef<am5.Root | null>(null)

  useEffect(() => {
    if (!stats?.dailyByCarrier?.length || !chartContainerRef.current) return

    const root = am5.Root.new(chartContainerRef.current)
    chartRef.current = root

    root.setThemes([am5themes_Animated.new(root)])
    root.interfaceColors.set('text', am5.color(0xffffff))
    root.interfaceColors.set('grid', am5.color(0x444444))

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: true,
        panY: true,
        wheelX: 'panX',
        wheelY: 'zoomX',
        pinchZoomX: true,
        layout: root.verticalLayout,
      }),
    )

    const cursor = chart.set(
      'cursor',
      am5xy.XYCursor.new(root, {
        behavior: 'none',
      }),
    )
    cursor.lineY.set('visible', false)

    const stepMinutes = stats.stepMinutes
    const isHourly = stepMinutes < 1440
    const baseInterval = isHourly
      ? stepMinutes < 60
        ? { timeUnit: 'minute' as const, count: stepMinutes }
        : { timeUnit: 'hour' as const, count: 1 }
      : { timeUnit: 'day' as const, count: 1 }
    const xAxis = chart.xAxes.push(
      am5xy.DateAxis.new(root, {
        maxDeviation: 0.2,
        baseInterval,
        renderer: am5xy.AxisRendererX.new(root, {
          minGridDistance: 50,
          cellStartLocation: 0.1,
          cellEndLocation: 0.9,
        }),
        tooltip: am5.Tooltip.new(root, {}),
      }),
    )

    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererY.new(root, {}),
        min: 0,
      }),
    )

    const carriers = new Set<string>()
    stats.dailyByCarrier.forEach(item => {
      item.byCarrier.forEach(carrier => {
        carriers.add(carrier.carrierCode)
      })
    })
    const carrierList = Array.from(carriers).sort()

    const processedData = stats.dailyByCarrier.map(item => {
      const [datePart, timePart] = item.date.split(' ')
      const [year, month, day] = datePart.split('-').map(Number)
      const [hour, minute] = timePart ? timePart.split(':').map(Number) : [0, 0]
      const dataItem: Record<string, number> & { date: number } = {
        date: Date.UTC(year, month - 1, day, hour, minute),
      }

      carrierList.forEach(carrier => {
        dataItem[carrier] = 0
      })

      item.byCarrier.forEach(carrier => {
        dataItem[carrier.carrierCode] = carrier.count
      })

      return dataItem
    })

    carrierList.forEach((carrier, index) => {
      const series = chart.series.push(
        am5xy.ColumnSeries.new(root, {
          name: carrier.toUpperCase(),
          xAxis,
          yAxis,
          valueYField: carrier,
          valueXField: 'date',
          stacked: true,
        }),
      )

      const color = getSeriesColor(index, carrierList.length)

      series.columns.template.setAll({
        fill: color,
        stroke: color,
        tooltipText: '[bold]{name}[/]: {valueY}',
        tooltipY: 0,
      })
      series.data.setAll(processedData)
      series.appear()
    })

    const legend = chart.children.push(
      am5.Legend.new(root, {
        centerX: am5.p50,
        x: am5.p50,
        marginTop: 15,
        marginBottom: 15,
      }),
    )

    legend.data.setAll(chart.series.values)

    chart.appear(1000, 100)

    return () => {
      root.dispose()
    }
  }, [stats])

  return (
    <ReportDataWrapper
      isLoading={isLoading}
      error={error}
      loadingMessage="Loading carrier activity..."
      onRetry={() => refresh()}
    >
      <div className="space-y-6">
        <AdminFilters isLoading={isLoading} onRefresh={refresh}>
          <ReportFilterControls
            filters={localFilters}
            onChange={setLocalFilters}
          >
            <CarrierRequestServiceMultiSelect
              value={localFilters.service}
              onApply={service =>
                setLocalFilters({
                  ...localFilters,
                  service,
                })
              }
            />
          </ReportFilterControls>
        </AdminFilters>

        {stats && stats.dailyByCarrier.length > 0 && (
          <div className="card bg-base-100 shadow-xl">
            <div className="card-body">
              <h2 className="card-title">Requests per Carrier</h2>
              <div
                ref={chartContainerRef}
                style={{ width: '100%', height: '500px' }}
              ></div>
            </div>
          </div>
        )}

        {!stats && !isLoading && !error && (
          <div className="alert">
            <span>
              No carrier activity data available for the selected range.
            </span>
          </div>
        )}
      </div>
    </ReportDataWrapper>
  )
}

export { CarrierRequestsReport }
